import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

/**
 * The button: rounded, sentence case. Blue for the one action a screen exists
 * for, soft grey for everything else (27 Oct 2026, the Trello look).
 *
 * Labels say what the button does and keep the same word all the way through
 * the flow — the button that says "Send quote" produces a toast that says
 * "Quote sent". That is a copy rule, but it is enforced here by making
 * `loadingLabel` a first-class prop so nobody reaches for a generic spinner.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const VARIANTS: Record<Variant, string> = {
  // Blue. One per screen, on the action the screen exists for.
  primary: 'bg-accent text-on-accent hover:bg-accent-strong disabled:hover:bg-accent',
  // The default: a soft grey button.
  secondary: 'bg-[var(--color-wash-strong)] text-ink hover:bg-[var(--color-rule)]',
  // For dense rows where a filled button would be noise.
  ghost: 'bg-transparent text-ink hover:bg-[var(--color-wash-strong)]',
  danger: 'bg-[var(--color-clay-wash)] text-clay hover:bg-clay hover:text-white',
}

const SIZES: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-[length:var(--text-xs)]',
  md: 'h-[var(--size-control)] px-3.5 text-[length:var(--text-sm)]',
}

const BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-[family-name:var(--font-heading)] ' +
  'font-medium leading-none rounded-md ' +
  'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] ' +
  'disabled:opacity-40 disabled:cursor-not-allowed select-none'

interface CommonProps {
  variant?: Variant
  size?: Size
  loading?: boolean
  /** Shown in place of the label while `loading`. Defaults to the label itself. */
  loadingLabel?: string
  icon?: ReactNode
  children: ReactNode
}

export type ButtonProps = CommonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, loadingLabel, icon, children, className, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...rest}
    >
      {loading ? <Spinner size={12} /> : icon}
      {loading && loadingLabel ? loadingLabel : children}
    </button>
  )
})

export type ButtonLinkProps = CommonProps & Omit<LinkProps, 'children'>

/** The same object, when the action is a navigation. */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  className,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
      {icon}
      {children}
    </Link>
  )
}
